import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card, { CardNames } from '../../../components/Card/Card.tsx';
import { PaymentMethodsByIdAtom, usePaymentMethods, useRestaurantId } from '../lib/state.ts';
import { getPhone } from '@/lib/getPhone.ts';
import MapView from '@/components/MapView/MapView.tsx';
import { getUserCredits, getRestaurant } from '@/lib/api.ts';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { AuthAtom, RestaurantAtom, useWidgetState, useUserDetails } from '../lib/state.ts';
import {
  getPaymentMethodIconName,
  getPaymentMethodLast4,
  isBankPaymentMethod,
} from '../lib/paymentTypes.ts';
import type {
  useBookingPreview,
  BookingPreview,
  BookingPreviewRestaurant,
} from '../lib/hooks/useBookingPreview.ts';
import { useAnnounce } from '../lib/announcer.ts';
import {
  getRestaurantImage,
  type PaymentMethod,
  type RestaurantResponseData,
} from '@/lib/types.ts';
import { useSubmitBooking } from '../lib/hooks/useSubmitBooking.ts';
import { IDV_REQUIRED_COPY } from '@/lib/apiErrorCode.ts';
import {
  formatRestaurantCurrency,
  isRestaurantTableMinimum,
  parseRestaurantCurrency,
  restaurantAmountDueApplies,
  restaurantTableMinimumCurrency,
  restaurantTableMinimumFromWidget,
  showsMinimumSpendExplanation,
} from '@/lib/labels.ts';
import {
  RestaurantAmountDueRow,
  RestaurantMinimumSpendTotals,
} from '../Payment/RestaurantMinimumSpendTotals.tsx';
import styles from '../EventPage/EventPage.module.css';
import checkoutStyles from './CheckoutPage.module.css';
import Config from '@/lib/Config.ts';
import { TERMS_OF_USE, PRIVACY_POLICY } from './legalContent.ts';
import { ContentModal } from './ContentModal.tsx';
import { EventCheckoutGate } from '../Payment/EventCheckoutGate.tsx';

/**
 * Renders a merchandising opt-in label with "Terms of Use" and "Privacy Policy"
 * replaced by clickable buttons that open their respective modals.
 */
function MerchandisingLabelWithLinks({
  label,
  onTermsClick,
  onPrivacyClick,
  linkClassName,
}: {
  label: string;
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  linkClassName: string;
}) {
  // Split on "Terms of Use" and "Privacy Policy" to inject clickable buttons
  const termsPattern = /Terms of Use/i;
  const privacyPattern = /Privacy Policy/i;

  const parts: Array<string | React.ReactNode> = [];
  let remaining = label;
  let key = 0;

  const termsMatch = termsPattern.exec(remaining);
  if (termsMatch) {
    parts.push(remaining.slice(0, termsMatch.index));
    parts.push(
      <button key={key++} type="button" className={linkClassName} onClick={onTermsClick}>
        {termsMatch[0]}
      </button>
    );
    remaining = remaining.slice(termsMatch.index + termsMatch[0].length);
  }

  const privacyMatch = privacyPattern.exec(remaining);
  if (privacyMatch) {
    parts.push(remaining.slice(0, privacyMatch.index));
    parts.push(
      <button key={key++} type="button" className={linkClassName} onClick={onPrivacyClick}>
        {privacyMatch[0]}
      </button>
    );
    remaining = remaining.slice(privacyMatch.index + privacyMatch[0].length);
  }

  parts.push(remaining);
  return <>{parts}</>;
}

/**
 * OrderSummarySidebar - Right sidebar showing order details and price breakdown.
 * Renders from the unified BookingPreview shape for both event and restaurant bookings.
 */
function OrderSummarySidebar({
  preview,
  widget,
  restaurantFallback,
  useCredits = true,
  showMinimumSpendExplanation = true,
  paymentMethod = null,
}: {
  preview: BookingPreview | null;
  widget: ReturnType<typeof useWidgetState>[0];
  restaurantFallback?: BookingPreviewRestaurant | null;
  useCredits?: boolean;
  /** True shows the food-and-drink caption. Defaults on when the hide flag is absent. */
  showMinimumSpendExplanation?: boolean;
  paymentMethod?: Pick<PaymentMethod, 'brand' | 'number'> | null;
}) {
  const rest = preview?.restaurant ?? restaurantFallback;
  const isEvent = widget.bookingType === 'event';
  const restaurantImage =
    getRestaurantImage(preview?.restaurant) ?? getRestaurantImage(restaurantFallback);
  const restPhone = rest && 'phone' in rest ? rest.phone : undefined;
  const restWebsite = rest && 'website' in rest ? rest.website : undefined;
  const rest_phone = getPhone(restPhone as string | undefined);

  // For event bookings, use preview items; for restaurant bookings, build a single item.
  // Group identical items by name so "Peter × 5" replaces five separate cards.
  const rawItems = preview?.items ?? [];
  const items = useMemo(() => {
    if (!isEvent) return rawItems;
    const grouped = new Map<string, (typeof rawItems)[0] & { totalQuantity: number }>();
    for (const item of rawItems) {
      const key = item.name ?? '';
      const existing = grouped.get(key);
      if (existing) {
        existing.totalQuantity += item.quantity ?? 1;
      } else {
        grouped.set(key, { ...item, totalQuantity: item.quantity ?? 1 });
      }
    }
    return Array.from(grouped.values());
  }, [rawItems, isEvent]);
  const hasItems = items.length > 0;
  const taxAmount = preview?.tax_amount ?? 0;
  const taxRate = preview?.tax_rate ?? 0;

  return (
    <div className={styles.orderSummary}>
      {rest && (
        <>
          <p className={styles.orderSummaryTitle}>{rest.name}</p>
          {rest.address && <p className={styles.orderSummaryAddress}>{rest.address}</p>}
          {'neighborhood_name' in rest && rest.neighborhood_name && (
            <p className={styles.orderSummaryAddress}>{rest.neighborhood_name}</p>
          )}
        </>
      )}

      <div className={styles.orderSummaryDivider} />

      {/* Booking items */}
      {hasItems ? (
        items.map((item, index) => {
          const itemImage = item.image ?? (!isEvent ? restaurantImage : undefined);
          const qty =
            'totalQuantity' in item
              ? (item as { totalQuantity: number }).totalQuantity
              : (item.quantity ?? 1);
          return (
            <div key={index} className={styles.orderSummaryBooking}>
              {itemImage && (
                <img src={itemImage} alt={item.name ?? ''} className={styles.orderSummaryImage} />
              )}
              <div className={styles.orderSummaryBookingDetails}>
                <p className={styles.orderSummaryBookingName}>
                  {item.name}
                  {isEvent && qty > 1 ? ` × ${qty}` : ''}
                </p>
                {isEvent && widget.event?.datetime_local__formatted?.date_long && (
                  <p className={styles.orderSummaryBookingInfo}>
                    {widget.event.datetime_local__formatted.date_dow_long},{' '}
                    {widget.event.datetime_local__formatted.date_long}
                  </p>
                )}
                {isEvent && widget.event?.datetime_local__formatted?.time && (
                  <p className={styles.orderSummaryBookingInfo}>
                    {widget.event.datetime_local__formatted.time}
                  </p>
                )}
                {!isEvent && (preview?.date_formatted || widget.confirm_body?.date) && (
                  <p className={styles.orderSummaryBookingInfo}>
                    {preview?.date_formatted ??
                      dayjs(widget.confirm_body?.date).format('ddd, MMMM D')}
                  </p>
                )}
                {!isEvent && (preview?.time_formatted || widget.confirm_body?.time) && (
                  <p className={styles.orderSummaryBookingInfo}>
                    {preview?.time_formatted ?? widget.confirm_body?.time}
                  </p>
                )}
                {!isEvent && item.quantity && item.quantity > 0 && (
                  <p className={styles.orderSummaryBookingInfo}>
                    {item.quantity} guest{item.quantity > 1 ? 's' : ''}
                  </p>
                )}
                {item.price__currency && (
                  <p className={styles.orderSummaryBookingInfo}>{item.price__currency}</p>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className={styles.orderSummaryBooking}>
          <div className={styles.orderSummaryBookingDetails}>
            <p className={styles.orderSummaryBookingName}>{widget.event?.name ?? rest?.name}</p>
            {preview?.party_size && preview.party_size > 0 && (
              <p className={styles.orderSummaryBookingInfo}>
                {preview.party_size} guest{preview.party_size > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      <div className={styles.orderSummaryDivider} />

      {/* Pricing rows */}
      {(() => {
        const totalPaid = preview?.total_paid ?? 0;
        const widgetMinimum = restaurantTableMinimumFromWidget({
          isEvent,
          pricePerPerson: widget.option?.price,
          partySize: widget.confirm_body?.party,
          formattedPricePerPerson: widget.option?.price__currency,
        });
        const feeRestaurant = preview?.fee_restaurant || widgetMinimum?.amount || 0;
        const feeCurrency = preview?.fee_restaurant__currency ?? widgetMinimum?.currency;
        const pricePerPerson = preview?.price_per_person ?? widget.option?.price;
        const pricePerPersonCurrency =
          preview?.price_per_person__currency ?? widget.option?.price__currency;
        const isMinSpend = isRestaurantTableMinimum({
          isEvent,
          totalPaid,
          feeRestaurant,
          feeCurrency,
          pricePerPerson,
        });
        const hasLineItems =
          (!isMinSpend && !!preview?.fee_restaurant__currency) ||
          taxAmount > 0 ||
          (preview?.service_charges?.length ?? 0) > 0 ||
          (useCredits &&
            (!!preview?.credits_used__currency || !!preview?.points_used_currency_value__currency));

        // When credits are unchecked, reconstruct the gross (before credit/fun coupon
        // deductions). credits_used is in dollars. Fun Coupons come from the raw points
        // (100 = $1) when present, else from the formatted deduction string: the restaurant
        // confirm payload leaves points_used_value empty, so without this the gross dropped
        // the Fun Coupons and the card charge read too low.
        const funCouponsDeducted =
          preview?.points_used_value != null
            ? preview.points_used_value / 100
            : (parseRestaurantCurrency(preview?.points_used_currency_value__currency) ?? 0);
        const displayTotal = useCredits
          ? totalPaid
          : totalPaid + (preview?.credits_used ?? 0) + funCouponsDeducted;
        const displayTotalCurrency = useCredits
          ? (preview?.total_paid__currency ?? widget.option?.price__currency)
          : formatRestaurantCurrency(
              displayTotal,
              preview?.total_paid__currency ?? widget.option?.price__currency
            );
        const walletApplied =
          useCredits &&
          (!!preview?.credits_used__currency || !!preview?.points_used_currency_value__currency);
        const cardName = paymentMethod ? getPaymentMethodIconName(paymentMethod) : null;
        const paymentMethodLast4 = paymentMethod ? getPaymentMethodLast4(paymentMethod) : null;

        return (
          <>
            {/* For event bookings or paid bookings, show subtotal */}
            {!isMinSpend && preview?.fee_restaurant__currency && (
              <div className={styles.orderSummaryPriceRow}>
                <p className={styles.orderSummaryPriceLabel}>Subtotal</p>
                <p className={styles.orderSummaryPriceValue}>{preview.fee_restaurant__currency}</p>
              </div>
            )}
            {taxAmount > 0 && (
              <div className={styles.orderSummaryPriceRow}>
                <p className={styles.orderSummaryPriceLabel}>Tax ({taxRate}%)</p>
                <p className={styles.orderSummaryPriceValue}>{preview?.tax_amount__currency}</p>
              </div>
            )}
            {preview?.service_charges?.map((service, index) => (
              <div key={index} className={styles.orderSummaryPriceRow}>
                <p className={styles.orderSummaryPriceLabel}>
                  {service.label}
                  {service.label === 'Cancellation Deposit' && (
                    <span className="mt-0.5 block text-sm text-black/60">
                      (Applied to your final bill)
                    </span>
                  )}
                </p>
                <p className={styles.orderSummaryPriceValue}>{service.amount__currency}</p>
              </div>
            ))}

            {walletApplied && (
              <div className={styles.orderSummaryWalletLines}>
                {preview?.credits_used__currency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Credits</p>
                    <p className={styles.orderSummaryPriceValue}>
                      {preview.credits_used__currency}
                    </p>
                  </div>
                )}
                {preview?.points_used_currency_value__currency && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Fun Coupons</p>
                    <p className={styles.orderSummaryPriceValue}>
                      -{preview.points_used_currency_value__currency}
                    </p>
                  </div>
                )}
              </div>
            )}
            {/* Amount Due is the card charge. Show it when the wallet reduces the
                charge, or when the charge differs from the bold Minimum spend (e.g.
                prepaid tax or service charges). A charge equal to the minimum with no
                wallet needs no row. */}
            {isMinSpend &&
              displayTotalCurrency &&
              restaurantAmountDueApplies({
                walletApplied,
                cardChargeAmount: displayTotal,
                pricePerPerson,
                partySize: preview?.party_size ?? widget.confirm_body?.party,
                feeRestaurant,
              }) && (
                <RestaurantAmountDueRow
                  amountDueCurrency={displayTotalCurrency}
                  cardName={cardName}
                  cardLast4={paymentMethodLast4}
                />
              )}

            {hasLineItems && <div className={styles.orderSummaryDivider} />}

            {/* Total / Minimum spend */}
            {isMinSpend ? (
              <RestaurantMinimumSpendTotals
                pricePerPersonCurrency={pricePerPersonCurrency}
                minimumSpendCurrency={restaurantTableMinimumCurrency({
                  pricePerPerson,
                  partySize: preview?.party_size ?? widget.confirm_body?.party,
                  feeRestaurant,
                  feeCurrency,
                })}
                showCaption={showMinimumSpendExplanation}
                paymentMethodLast4={paymentMethodLast4}
                paymentMethodCardName={cardName}
              />
            ) : preview ? (
              <div className={styles.orderSummaryTotal}>
                <p className={styles.orderSummaryTotalLabel}>Total</p>
                <p className={styles.orderSummaryTotalValue}>{displayTotalCurrency}</p>
              </div>
            ) : null}
          </>
        );
      })()}

      {/* Map — only when lat/lon are available from the full restaurant data */}
      {rest?.address && 'latitude' in rest && 'longitude' in rest && (
        <MapView restaurant={rest as Parameters<typeof MapView>[0]['restaurant']} height={200} />
      )}

      {/* Phone + Website */}
      {(rest_phone || restWebsite) && (
        <div className={styles.contactLinks}>
          {rest_phone && (
            <a href={rest_phone.getURI()} className={styles.contactLink}>
              {rest_phone.formatNational()}
            </a>
          )}
          {restWebsite && (
            <a
              href={restWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.contactLink}
            >
              {restWebsite.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * CheckoutPage - Unified checkout component for both event and restaurant bookings.
 *
 * Uses useBookingPreview() for pricing data and useSubmitBooking() for reservation completion.
 * Routes to the correct API based on widget.bookingType.
 *
 * Features:
 * - Two-column layout with order summary sidebar
 * - ContentModal for T&C, Dorsia Terms of Use, Privacy Policy
 * - Restaurant-specific expanded T&C (aeg_terms_accepted)
 * - Alternative pickup display (event bookings)
 * - Dorsia Wallet credits check
 * - Merchandising policy display
 * - Marketing opt-in conditional on merchandising_marketing_opt_in_label
 * - Cancellation deposit label
 */
const CheckoutPage = () => (
  <EventCheckoutGate>
    {(bookingPreview) => <DorsiaCheckoutPage bookingPreview={bookingPreview} />}
  </EventCheckoutGate>
);

const DorsiaCheckoutPage = ({
  bookingPreview,
}: {
  bookingPreview: ReturnType<typeof useBookingPreview>;
}) => {
  usePaymentMethods();
  const navigate = useNavigate();
  const auth = useAtomValue(AuthAtom);
  const [widget, updateWidget] = useWidgetState();
  const user = useUserDetails();
  const methods_by_id = useAtomValue(PaymentMethodsByIdAtom);
  const id = useRestaurantId();
  const cachedRestaurant = useAtomValue(RestaurantAtom);
  const redirectedToBookingRef = useRef(false);

  // Stripe for 3D Secure
  const [stripe, setStripe] = useState<Stripe | null>(null);

  // Credits — default ON per HOS-1031
  const [creditsAvailable, setCreditsAvailable] = useState<boolean>(false);
  const [useCredits, setUseCredits] = useState<boolean>(true);
  const [creditsAmount, setCreditsAmount] = useState<string | null>(null);
  const [funCouponsCount, setFunCouponsCount] = useState<number>(0);

  // Modal state
  const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
  const [dorsiaTermsOpen, setDorsiaTermsOpen] = useState<boolean>(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState<boolean>(false);

  // Fetched restaurant for fallback (full getRestaurant payload — carries features)
  const [fetchedRestaurant, setFetchedRestaurant] = useState<RestaurantResponseData | null>(null);

  // Unified preview hook
  const {
    preview,
    loading: previewLoading,
    error: previewError,
    errorCode: previewErrorCode,
  } = bookingPreview;

  // Unified submit hook
  const {
    submit,
    loading: submitLoading,
    error: submitError,
    errorCode: submitErrorCode,
  } = useSubmitBooking({ stripe, useCredits });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(widget?.payment_error);

  // Use whichever restaurant source has data
  const restaurant = preview?.restaurant ?? cachedRestaurant ?? fetchedRestaurant;

  const isEvent = widget.bookingType === 'event';

  // Feature flags + cancellation policy only exist on the full getRestaurant payload,
  // not on the booking preview's slimmed-down restaurant block.
  const fullRestaurant = cachedRestaurant ?? fetchedRestaurant;
  const cancellationPolicyText = fullRestaurant?.cancellation_policy;
  // A venue is only treated as cancellable when the flag is on AND a policy text
  // exists — otherwise we fall back to the no-refund checkbox so the checkout
  // never silently drops all refund messaging.
  const allowsCancellation =
    isEvent && !!fullRestaurant?.features?.WidgetCancelReservation && !!cancellationPolicyText;
  const payment_method = methods_by_id[widget.payment_method!];

  const isZeroDollarNoPayment =
    isEvent &&
    (widget.option?.price ?? -1) === 0 &&
    !!cachedRestaurant?.allow_zero_dollar_booking_links_without_payment_method;
  const user_phone = getPhone(widget.phone_number, widget.phone_country);

  // Dynamic T&C from restaurant data
  const expandedTcTitle = restaurant?.expanded_tc_title;
  const expandedTc = restaurant?.expanded_tc;
  const expandedTcLabel = restaurant?.expanded_tc_opt_in_label;

  // Merchandising policy from preview
  const showMerchandisingPriceTotals = preview?.restaurant?.show_merchandising_price_totals;
  const showMerchandisingTaxAndTip = preview?.restaurant?.show_merchandising_tax_and_tip;
  const merchandisingPolicy = preview?.restaurant?.merchandising_policy;
  const merchandisingPolicyTitle = preview?.restaurant?.merchandising_policy_title;
  const merchandisingPolicyOptInLabel = preview?.restaurant?.merchandising_policy_opt_in_label;
  const merchandisingMarketingOptInLabel =
    preview?.restaurant?.merchandising_marketing_opt_in_label;

  // Disclosure text from restaurant config (event bookings)
  const disclosureText = restaurant?.disclosure_text;
  const disclosureLinkUrl = restaurant?.disclosure_link_url;
  const disclosureLinkLabel = restaurant?.disclosure_link_label;

  // Guard: redirect if missing booking type or auth
  useEffect(() => {
    if (redirectedToBookingRef.current) return;

    if (!widget.bookingType) {
      redirectedToBookingRef.current = true;
      updateWidget({
        reservation_error: 'Please complete your booking selection before continuing.',
      });
      navigate('../booking', { replace: true });
      return;
    }

    if (!auth?.token) {
      redirectedToBookingRef.current = true;
      navigate('../details', { replace: true });
    }
  }, [widget.bookingType, auth?.token, updateWidget, navigate]);

  // Load Stripe + check credits
  useEffect(() => {
    const initStripe = async () => {
      setStripe(await loadStripe(Config.data.stripe.key));
    };
    void initStripe();

    if (auth?.token) {
      void onCheckCredits();
    }

    // Fetch the full restaurant payload whenever the atom is empty — the preview's
    // restaurant block lacks features/cancellation_policy, so we can't rely on it.
    if (id && !cachedRestaurant) {
      const today = new Date().toISOString().split('T')[0];
      getRestaurant(id, today, '0:00', `checkout-${id}`, auth?.token ?? null)
        .then((res) => {
          if (res?.data) {
            setFetchedRestaurant(res.data);
          }
        })
        .catch((e) => Sentry.captureException(e));
    }
  }, [auth?.token, id, cachedRestaurant]);

  const onCheckCredits = async () => {
    if (!auth?.token) {
      return;
    }

    const user_credits_response = await getUserCredits(auth.token);
    if (!user_credits_response) {
      return;
    }

    const credits = user_credits_response.data.credits?.USD;
    const points = user_credits_response.data.points ?? 0;
    const hasCredits = credits !== undefined && credits !== '$0.00';
    const hasPoints = points > 0;

    setCreditsAvailable(hasCredits || hasPoints);
    if (hasCredits && credits) setCreditsAmount(credits);
    if (hasPoints) setFunCouponsCount(points);
  };

  const canSubmit = useMemo(() => {
    // Events always require dorsia_terms_accepted; also require policy_accepted when venue
    // has a custom merchandising_policy_opt_in_label (venue-specific legal language).
    // Restaurants always require policy_accepted.
    const dorsiaAccepted = isEvent
      ? widget.dorsia_terms_accepted === true &&
        (merchandisingPolicyOptInLabel ? widget.policy_accepted === true : true)
      : widget.policy_accepted === true;
    // Only require expanded T&C acceptance if the restaurant has them
    const tcAccepted = expandedTc ? widget.aeg_terms_accepted === true : true;
    // For $0 free events with the flag, no payment method is required
    const paymentReady = isZeroDollarNoPayment || !!payment_method;
    // Event-specific: require the refund-policy acknowledgment. The checkbox label is
    // either the hard-coded no-refund text or the venue's cancellation policy.
    const noRefundAccepted = isEvent ? widget.no_refund_accepted === true : true;
    return dorsiaAccepted && tcAccepted && noRefundAccepted && paymentReady && !!widget.bookingType;
  }, [
    widget.policy_accepted,
    widget.aeg_terms_accepted,
    widget.dorsia_terms_accepted,
    widget.no_refund_accepted,
    expandedTc,
    payment_method,
    widget.bookingType,
    isZeroDollarNoPayment,
    isEvent,
    allowsCancellation,
    merchandisingPolicyOptInLabel,
  ]);

  const onNext = () => {
    if (!canSubmit) {
      if (!isEvent && !widget.policy_accepted) {
        updateWidget({ policy_accepted: false });
      } else if (isEvent && merchandisingPolicyOptInLabel && !widget.policy_accepted) {
        updateWidget({ policy_accepted: false });
      } else if (!payment_method && !isZeroDollarNoPayment) {
        setErrorMessage('Select a payment method before completing booking.');
      }
      return;
    }

    setErrorMessage(undefined);
    void submit();
  };

  const onToggleCredits = (checked: boolean) => {
    setUseCredits(checked);
  };

  const onMarketing = (checked: boolean) => {
    updateWidget({
      opt_in_accepted: checked ? 1 : 0,
    });
  };

  const onAcceptAegTerms = (checked: boolean) => {
    updateWidget({
      aeg_terms_accepted: checked,
    });
  };

  const onAcceptDorsiaTerms = (checked: boolean) => {
    updateWidget({
      policy_accepted: checked || null,
    });
  };

  const onAcceptDorsiaEventTerms = (checked: boolean) => {
    updateWidget({ dorsia_terms_accepted: checked });
  };

  const onAcceptNoRefund = (checked: boolean) => {
    updateWidget({ no_refund_accepted: checked });
  };

  // Combine errors from all sources — surface a friendly message for preview errors.
  // If the backend returns error_code: 'IDV_REQUIRED' (from either preview or submit),
  // show IDV-specific guidance instead of the generic fallback.
  const idvRequired = submitErrorCode === 'IDV_REQUIRED' || previewErrorCode === 'IDV_REQUIRED';
  const displayError = idvRequired
    ? IDV_REQUIRED_COPY
    : (errorMessage ??
      submitError ??
      (previewError
        ? 'This item is currently unavailable for booking. Please go back and select a different option.'
        : undefined));

  // Announce a booking/submit failure to assistive tech (no-op in the SPA).
  const announce = useAnnounce();
  useEffect(() => {
    if (displayError) announce(displayError);
  }, [displayError, announce]);

  return (
    <>
      {/* Dynamic T&C modal -- content from restaurant.expanded_tc */}
      <ContentModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title={expandedTcTitle ?? 'Terms & Conditions'}
        content={expandedTc ?? ''}
      />

      {/* Dorsia Terms of Use modal */}
      <ContentModal
        isOpen={dorsiaTermsOpen}
        onClose={() => setDorsiaTermsOpen(false)}
        title="Terms of Use"
        content={TERMS_OF_USE}
      />

      {/* Dorsia Privacy Policy modal */}
      <ContentModal
        isOpen={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        title="Privacy Policy"
        content={PRIVACY_POLICY}
      />

      {!isEvent && widget.policy_accepted === false && (
        <div className={styles.error} style={{ textAlign: 'center', marginBottom: 16 }}>
          You are missing required fields.
        </div>
      )}

      <div className={`${styles.twoColumnLayout} ${styles.summaryAsideLayout}`}>
        <div className={styles.summaryAsideStack}>
          <div className={styles.summaryAsideLead}>
            {widget.eventInfo?.requires_idv && (
              <div className={styles.infoBanner}>
                Identity verification is required for this booking. You&apos;ll receive instructions
                by text after confirming your reservation.
              </div>
            )}
            {/* Collapsed Details section */}
            <div className={styles.card}>
              <div className={styles.contactSummary}>
                <div className={styles.contactSummaryHeader}>
                  <h2 className={styles.contactSummaryTitle}>Details</h2>
                  <Link to={'../details'} className={styles.editLink}>
                    Edit
                  </Link>
                </div>
                <div className={styles.contactColumns}>
                  <div className={styles.contactColumn}>
                    <p className={styles.contactLabel}>MEMBER DETAILS</p>
                    <p className={styles.contactValue}>
                      {user.first_name} {user.last_name}
                    </p>
                    <p className={styles.contactValue}>
                      {!!user_phone && user_phone.formatNational()}
                    </p>
                    <p className={styles.contactValue}>{user.email}</p>
                  </div>
                  {cachedRestaurant?.allow_alternative_pickup &&
                    widget.alternative_pickup_first_name && (
                      <div className={styles.contactColumn}>
                        <p className={styles.contactLabel}>ALTERNATE PICKUP</p>
                        <p className={styles.contactValue}>
                          {widget.alternative_pickup_first_name}{' '}
                          {widget.alternative_pickup_last_name}
                        </p>
                        <p className={styles.contactValue}>{widget.alternative_pickup_phone}</p>
                        <p className={styles.contactValue}>{widget.alternative_pickup_email}</p>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Collapsed Payment section */}
            <div className={`${styles.card} ${styles.mt20}`}>
              <div className={styles.contactSummary}>
                <div className={styles.contactSummaryHeader}>
                  <h2 className={styles.contactSummaryTitle}>Payment</h2>
                  {!isZeroDollarNoPayment && (
                    <Link to={'../payment'} className={styles.editLink}>
                      Edit
                    </Link>
                  )}
                </div>
                {isZeroDollarNoPayment ? (
                  <p className={styles.contactValue} style={{ color: '#8d8d8d' }}>
                    No payment required for this booking.
                  </p>
                ) : (
                  <div className={styles.contactColumns}>
                    {widget.ach_bank_last4 && (
                      <div className={styles.contactColumn}>
                        <p className={styles.contactLabel}>PLAID ACH</p>
                        <p className={styles.contactValue}>
                          {widget.ach_bank_name ?? 'Bank'} &bull;&bull;&bull;&bull;
                          {widget.ach_bank_last4}
                        </p>
                      </div>
                    )}
                    {payment_method && (
                      <div className={styles.contactColumn}>
                        <p className={styles.contactLabel}>
                          {isBankPaymentMethod(payment_method) ? 'BANK ACCOUNT' : 'CREDIT CARD'}
                        </p>
                        <div className="flex items-center gap-3">
                          <div className="w-10 flex-shrink-0">
                            <Card name={getPaymentMethodIconName(payment_method) as CardNames} />
                          </div>
                          <p className={styles.contactValue}>
                            ending in {getPaymentMethodLast4(payment_method)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Merchandising policy (restaurant bookings) */}
            {!isEvent &&
              preview &&
              preview.total_paid > 0 &&
              (showMerchandisingPriceTotals || showMerchandisingTaxAndTip) && (
                <div className={`${styles.card} ${styles.mt20}`}>
                  <div style={{ padding: '0 0 8px' }}>
                    <h2
                      className={styles.contactSummaryTitle}
                      style={{ fontSize: 20, marginBottom: 12 }}
                    >
                      {merchandisingPolicyTitle ?? 'Cancellation and modification policy'}
                    </h2>

                    {merchandisingPolicy && (
                      <div
                        style={{
                          fontSize: 14,
                          lineHeight: 1.6,
                          color: '#303030',
                          marginBottom: 12,
                        }}
                      >
                        {merchandisingPolicy.split('\n').map((line, i) => (
                          <div key={i} style={{ marginBottom: 5 }}>
                            {line}
                          </div>
                        ))}
                      </div>
                    )}

                    {preview.cancellation_policies?.map((policy, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 14,
                          lineHeight: 1.6,
                          color: '#303030',
                          marginBottom: 5,
                        }}
                      >
                        {policy}
                      </div>
                    ))}

                    {preview.notices && preview.notices.length > 0 && (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'rgba(0,0,0,0.6)',
                          marginTop: 8,
                        }}
                      >
                        {preview.notices.map((notice, i) => (
                          <div key={i}>{notice}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Dorsia Wallet section — own card, above Review */}
            {creditsAvailable && (
              <div className={`${styles.card} ${styles.mt20}`}>
                <div className={styles.contactSummary}>
                  <div className={styles.contactSummaryHeader}>
                    <h2 className={styles.contactSummaryTitle}>Dorsia Wallet</h2>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {creditsAmount && (
                      <p className="m-0 text-sm text-gray-500">Credits: {creditsAmount}</p>
                    )}
                    {funCouponsCount > 0 && (
                      <p className="m-0 text-sm text-gray-500">
                        Fun Coupons: {funCouponsCount.toLocaleString()} ($
                        {(funCouponsCount / 100).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        )
                      </p>
                    )}
                  </div>
                  <div className={styles.checkboxRow} style={{ marginTop: 12 }}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        onToggleCredits(e.target.checked);
                      }}
                      checked={useCredits}
                      aria-label="Apply credits and Fun Coupons to this booking"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      Apply credits and Fun Coupons to this booking
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.summaryAsideTrail}>
            {/* Review and Reserve section */}
            <div className={`${styles.card} ${styles.mt20}`}>
              <div className={styles.reviewSection}>
                <h2 className={styles.contactSummaryTitle}>Review and Reserve</h2>
                <p className={styles.aboutText} style={{ color: '#8d8d8d', marginBottom: 20 }}>
                  Review your information and reserve when you&apos;re ready.
                </p>

                {previewLoading && <p style={{ color: '#8d8d8d', marginBottom: 16 }}>Loading...</p>}

                {/* Marketing opt-in — always shown for events (with fallback label), conditional for restaurants */}
                {(isEvent || merchandisingMarketingOptInLabel) && (
                  <div className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      onChange={(e) => onMarketing(e.target.checked)}
                      checked={widget.opt_in_accepted === 1}
                      aria-label="Receive marketing communications"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      {typeof merchandisingMarketingOptInLabel === 'string'
                        ? merchandisingMarketingOptInLabel
                        : "Yes, I'd like to receive marketing communications from this venue and its partners."}
                    </span>
                  </div>
                )}

                {/* Dynamic T&C from restaurant -- only show if expanded_tc exists */}
                {expandedTc && (
                  <div className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      onChange={(e) => onAcceptAegTerms(e.target.checked)}
                      checked={widget.aeg_terms_accepted === true}
                      aria-label="Accept the terms and conditions"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      {expandedTcLabel ?? 'I understand and accept the '}{' '}
                      <button
                        type="button"
                        className={checkoutStyles.tcLink}
                        onClick={(e) => {
                          e.preventDefault();
                          setTermsModalOpen(true);
                        }}
                      >
                        {expandedTcTitle ?? 'Terms and Conditions'}
                      </button>
                    </span>
                  </div>
                )}

                {/* Dorsia reservation policy — shown for restaurants always; shown for events only when
                  venue has custom merchandising_policy_opt_in_label (venue-specific legal language).
                  Hidden for events with no custom label to avoid duplication with Dorsia Terms below. */}
                {(!isEvent || merchandisingPolicyOptInLabel) && (
                  <div className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      onChange={(e) => onAcceptDorsiaTerms(e.target.checked)}
                      checked={widget.policy_accepted === true}
                      aria-label="Accept Dorsia's Terms of Use and Privacy Policy"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      {merchandisingPolicyOptInLabel ? (
                        <MerchandisingLabelWithLinks
                          label={merchandisingPolicyOptInLabel}
                          onTermsClick={() => setDorsiaTermsOpen(true)}
                          onPrivacyClick={() => setPrivacyPolicyOpen(true)}
                          linkClassName={checkoutStyles.tcLink}
                        />
                      ) : (
                        <>
                          I accept Dorsia&apos;s{' '}
                          <button
                            type="button"
                            className={checkoutStyles.tcLink}
                            onClick={() => setDorsiaTermsOpen(true)}
                          >
                            Terms of Use
                          </button>{' '}
                          and{' '}
                          <button
                            type="button"
                            className={checkoutStyles.tcLink}
                            onClick={() => setPrivacyPolicyOpen(true)}
                          >
                            Privacy Policy
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                )}

                {/* Event-specific: Dorsia Terms (purchase confirmation) */}
                {isEvent && (
                  <div className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      onChange={(e) => onAcceptDorsiaEventTerms(e.target.checked)}
                      checked={widget.dorsia_terms_accepted === true}
                      aria-label="Accept Dorsia's Terms of Use and Privacy Policy"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      I understand that the purchase of this access confirms your having received,
                      read, and comprehended Dorsia&apos;s{' '}
                      <button
                        type="button"
                        className={checkoutStyles.tcLink}
                        onClick={() => setDorsiaTermsOpen(true)}
                      >
                        Terms of Use
                      </button>{' '}
                      and{' '}
                      <button
                        type="button"
                        className={checkoutStyles.tcLink}
                        onClick={() => setPrivacyPolicyOpen(true)}
                      >
                        Privacy Policy
                      </button>
                      .
                    </span>
                  </div>
                )}

                {/* Event-specific: refund-policy acknowledgment. Cancellable venues show their
                  cancellation policy as the label; everyone else gets the no-refund text. */}
                {isEvent && (
                  <div className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      onChange={(e) => onAcceptNoRefund(e.target.checked)}
                      checked={widget.no_refund_accepted === true}
                      aria-label="Acknowledge the refund policy"
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxLabel}>
                      {allowsCancellation
                        ? cancellationPolicyText
                        : 'This transaction is not returnable or refundable. All sales are final.'}
                    </span>
                  </div>
                )}

                {/* Event-specific: Disclosure text (informational, no checkbox) */}
                {isEvent && disclosureText && (
                  <p
                    className={styles.checkboxLabel}
                    style={{
                      marginTop: 8,
                      marginBottom: 0,
                      color: '#8d8d8d',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    {disclosureLinkUrl && disclosureLinkLabel
                      ? disclosureText.split('{link}').map((segment, i, arr) =>
                          i < arr.length - 1 ? (
                            <span key={i}>
                              {segment}
                              <a
                                href={disclosureLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={checkoutStyles.tcLink}
                              >
                                {disclosureLinkLabel}
                              </a>
                            </span>
                          ) : (
                            <span key={i}>{segment}</span>
                          )
                        )
                      : disclosureText}
                  </p>
                )}

                {!isEvent && widget.policy_accepted === false && (
                  <div className={styles.error}>
                    You must accept the terms to complete your reservation.
                  </div>
                )}

                <button
                  className={styles.primaryButton}
                  onClick={onNext}
                  disabled={submitLoading || !canSubmit}
                  style={{ marginTop: 20 }}
                >
                  {submitLoading ? 'Processing...' : 'Reserve and Complete Booking'}
                </button>

                {displayError && <div className={styles.error}>{displayError}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.sidebarColumn}>
          <OrderSummarySidebar
            preview={preview}
            widget={widget}
            restaurantFallback={restaurant}
            useCredits={useCredits}
            showMinimumSpendExplanation={showsMinimumSpendExplanation(fullRestaurant?.features)}
            paymentMethod={isZeroDollarNoPayment ? null : payment_method}
          />
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;
